import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import api, { User } from '../services/api';

interface AuthContextType {
  user: User | null;
  supabaseUser: SupabaseUser | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string, userData?: any) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateEmail: (newEmail: string) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track last synced access token to prevent duplicate API calls
  const lastSyncedTokenRef = useRef<string | null>(null);

  // Sync Django user when Supabase session changes
  const syncDjangoUser = async (supabaseSession: Session | null, forceRefresh: boolean = false) => {
    // Ensure we have a valid token before proceeding
    const accessToken = supabaseSession?.access_token?.trim();
    if (!accessToken || accessToken.length === 0) {
      // Clear any stale auth headers if no valid token
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      lastSyncedTokenRef.current = null;
      return;
    }
    
    // Skip if we've already synced with this token (unless forced refresh)
    if (!forceRefresh && lastSyncedTokenRef.current === accessToken) {
      console.log('⏭️ Skipping duplicate Django sync - token already synced');
      return;
    }
    
    try {
      // Set the Supabase token in API headers for Django backend
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      // Fetch or create Django user profile
      const response = await api.get('/auth/status');
      if (response.data.authenticated && response.data.user) {
        setUser(response.data.user);
        lastSyncedTokenRef.current = accessToken;
        console.log('✅ Django user synced:', response.data.user.email);
      }
    } catch (error) {
      console.error('❌ Failed to sync Django user:', error);
      // User might not exist in Django yet - that's okay, middleware will create it
    }
  };

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get current session
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('❌ Session error:', sessionError);
          setError(sessionError.message);
        }

        if (currentSession) {
          setSession(currentSession);
          setSupabaseUser(currentSession.user);
          console.log('✅ Supabase session restored:', currentSession.user.email);
          await syncDjangoUser(currentSession, true); // Force refresh on page load
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('🔄 Auth state changed:', event, newSession?.user?.email);
        
        setSession(newSession);
        setSupabaseUser(newSession?.user || null);
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Only sync if we have a valid session with a token
          if (newSession?.access_token) {
            await syncDjangoUser(newSession);
          }
        } else if (event === 'INITIAL_SESSION') {
          // For INITIAL_SESSION, only sync if session exists and has token
          if (newSession?.access_token) {
            await syncDjangoUser(newSession, true); // Force refresh for initial session
          } else {
            // Clear any stale auth headers if no session
            delete api.defaults.headers.common['Authorization'];
            setUser(null);
            lastSyncedTokenRef.current = null;
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          lastSyncedTokenRef.current = null;
          delete api.defaults.headers.common['Authorization'];
        }
      }
    );
    
    // Refresh user profile when window gains focus (user switches back to tab)
    // This ensures data from other devices are picked up, but only if needed
    const handleWindowFocus = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession?.access_token) {
        console.log('[Auth Sync] Window focused - checking for profile updates');
        // Only sync if we don't already have this token synced
        // This prevents overwriting local changes with stale backend data
        if (lastSyncedTokenRef.current !== currentSession.access_token) {
          await syncDjangoUser(currentSession, true);
        }
      }
    };
    
    window.addEventListener('focus', handleWindowFocus);

    // Cleanup subscription and event listener on unmount
    return () => {
      subscription.unsubscribe();
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  // Login with email and password
  const login = async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (loginError) {
        console.error('❌ Supabase login error:', loginError);
        throw new Error(loginError.message);
      }

      if (data.session) {
        console.log('✅ Supabase login successful:', data.user?.email);
        // Auth state change listener will handle the rest
      }
    } catch (error: any) {
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    setError(null);
    
    try {
      const { error: logoutError } = await supabase.auth.signOut();
      
      if (logoutError) {
        console.error('❌ Supabase logout error:', logoutError);
        throw new Error(logoutError.message);
      }

      // Clear local state
      setUser(null);
      setSupabaseUser(null);
      setSession(null);
      delete api.defaults.headers.common['Authorization'];
      
      console.log('✅ Logged out successfully');
    } catch (error: any) {
      setError(error.message || 'Logout failed');
      throw error;
    }
  };

  // Register new user
  const register = async (email: string, password: string, userData?: any) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const { data, error: registerError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: userData?.first_name || '',
            last_name: userData?.last_name || '',
          },
        },
      });

      if (registerError) {
        console.error('❌ Supabase registration error:', registerError);
        throw new Error(registerError.message);
      }

      if (data.user) {
        console.log('✅ Supabase registration successful:', data.user.email);
        // Note: User may need to confirm email depending on Supabase settings
      }
    } catch (error: any) {
      setError(error.message || 'Registration failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Request password reset email
  const resetPassword = async (email: string) => {
    setError(null);
    
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) {
        console.error('❌ Password reset error:', resetError);
        throw new Error(resetError.message);
      }

      console.log('✅ Password reset email sent to:', email);
    } catch (error: any) {
      setError(error.message || 'Failed to send reset email');
      throw error;
    }
  };

  // Update password (after clicking reset link)
  const updatePassword = async (newPassword: string) => {
    setError(null);
    
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        console.error('❌ Password update error:', updateError);
        throw new Error(updateError.message);
      }

      console.log('✅ Password updated successfully');
    } catch (error: any) {
      setError(error.message || 'Failed to update password');
      throw error;
    }
  };

  // Update email address
  const updateEmail = async (newEmail: string) => {
    setError(null);
    
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        email: newEmail,
      });

      if (updateError) {
        console.error('❌ Email update error:', updateError);
        throw new Error(updateError.message);
      }

      console.log('✅ Email update initiated. Confirmation sent to:', newEmail);
    } catch (error: any) {
      setError(error.message || 'Failed to update email');
      throw error;
    }
  };

  const clearError = () => setError(null);

  const isAuthenticated = !!session && !!supabaseUser;

  return (
    <AuthContext.Provider
      value={{
        user,
        supabaseUser,
        session,
        isAuthenticated,
        isLoading,
        error,
        login,
        logout,
        register,
        resetPassword,
        updatePassword,
        updateEmail,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
